import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import {
  Search, UserPlus, X, Network, ChevronRight, Users, Loader2, Check,
} from 'lucide-react';
import { broadcastAPI } from '@/lib/api';

const initials = (name = '') =>
  name.split(/\s+/).filter(Boolean).slice(0, 2).map((p) => p[0]?.toUpperCase()).join('') || '?';

const AVATAR_TONES = [
  'bg-sky-100 text-sky-700', 'bg-violet-100 text-violet-700',
  'bg-emerald-100 text-emerald-700', 'bg-amber-100 text-amber-700',
  'bg-rose-100 text-rose-700', 'bg-indigo-100 text-indigo-700',
];
const toneFor = (s = '') => AVATAR_TONES[(s.charCodeAt(0) || 0) % AVATAR_TONES.length];

/**
 * Creative recipient picker — two interactive modes side by side:
 *  • Pick agents   — live search, click-to-add chips.
 *  • Agency downline — an org tree; selecting an agency targets it AND its
 *                      whole downline (every descendant agency + their agents).
 * Controlled: reports selections up via onChange handlers.
 */
export default function RecipientPicker({
  pickedAgents, onPickedAgentsChange,
  downlineAgencies, onDownlineChange,
}) {
  const [mode, setMode] = useState('agents');

  return (
    <div className="rounded-xl border border-slate-200 bg-gradient-to-b from-slate-50 to-white overflow-hidden"
         data-testid="recipient-picker">
      <div className="flex items-center gap-1 p-1 bg-slate-100/70 border-b border-slate-200">
        <PickerTab active={mode === 'agents'} onClick={() => setMode('agents')}
                   icon={UserPlus} label="Pick agents"
                   count={pickedAgents.length} testid="picker-tab-agents" />
        <PickerTab active={mode === 'downline'} onClick={() => setMode('downline')}
                   icon={Network} label="Agency downline"
                   count={downlineAgencies.length} testid="picker-tab-downline" />
      </div>
      <div className="p-3">
        {mode === 'agents'
          ? <AgentPicker picked={pickedAgents} onChange={onPickedAgentsChange} />
          : <DownlinePicker selected={downlineAgencies} onChange={onDownlineChange} />}
      </div>
    </div>
  );
}

function PickerTab({ active, onClick, icon: Icon, label, count, testid }) {
  return (
    <button
      type="button" onClick={onClick} data-testid={testid}
      className={`flex-1 inline-flex items-center justify-center gap-1.5 rounded-lg px-3 py-2 text-xs font-medium transition-all ${
        active ? 'bg-white text-slate-900 shadow-sm ring-1 ring-slate-200' : 'text-slate-500 hover:text-slate-700'}`}
    >
      <Icon size={14} className={active ? 'text-sky-600' : ''} /> {label}
      {count > 0 && (
        <span className="ml-0.5 inline-flex items-center justify-center min-w-[18px] h-[18px] rounded-full bg-sky-600 text-white text-[10px] px-1">{count}</span>
      )}
    </button>
  );
}

/* ───────────────────────── Agent search ───────────────────────── */
function AgentPicker({ picked, onChange }) {
  const [q, setQ] = useState('');
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const timer = useRef(null);

  const search = useCallback(async (term) => {
    setLoading(true);
    try {
      const r = await broadcastAPI.searchAgents(term);
      setResults(r.data.items || []);
    } catch { setResults([]); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => {
    if (timer.current) clearTimeout(timer.current);
    timer.current = setTimeout(() => search(q), 300);
    return () => timer.current && clearTimeout(timer.current);
  }, [q, search]);

  const pickedIds = useMemo(() => new Set(picked.map((p) => p.id)), [picked]);
  const add = (a) => { if (!pickedIds.has(a.id)) onChange([...picked, a]); };
  const remove = (id) => onChange(picked.filter((p) => p.id !== id));

  return (
    <div className="space-y-3">
      <div className="relative">
        <Search size={14} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-slate-400" />
        <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search by name, email or NPN…"
               className="pl-8 h-9 text-sm" data-testid="agent-search-input" />
      </div>

      {picked.length > 0 && (
        <div className="flex flex-wrap gap-1.5" data-testid="agent-chips">
          {picked.map((a) => (
            <span key={a.id} className="inline-flex items-center gap-1.5 rounded-full bg-sky-50 border border-sky-200 pl-1 pr-2 py-0.5 text-xs text-sky-800">
              <span className={`inline-flex items-center justify-center w-5 h-5 rounded-full text-[9px] font-semibold ${toneFor(a.name)}`}>{initials(a.name)}</span>
              {a.name}
              <button type="button" onClick={() => remove(a.id)} data-testid={`agent-chip-remove-${a.id}`}
                      className="text-sky-400 hover:text-sky-700"><X size={12} /></button>
            </span>
          ))}
          <button type="button" onClick={() => onChange([])} className="text-[11px] text-slate-400 hover:text-rose-500 underline ml-1">clear all</button>
        </div>
      )}

      <div className="max-h-56 overflow-y-auto rounded-lg border border-slate-100 divide-y divide-slate-50">
        {loading ? (
          <div className="p-4 text-center text-slate-400 text-xs"><Loader2 size={14} className="inline animate-spin mr-1" /> Searching…</div>
        ) : results.length === 0 ? (
          <div className="p-4 text-center text-slate-400 text-xs">No agents match “{q}”.</div>
        ) : results.map((a) => {
          const on = pickedIds.has(a.id);
          return (
            <button key={a.id} type="button" onClick={() => (on ? remove(a.id) : add(a))}
                    data-testid={`agent-result-${a.id}`}
                    className={`w-full flex items-center gap-2.5 px-3 py-2 text-left transition-colors ${on ? 'bg-sky-50/60' : 'hover:bg-slate-50'}`}>
              <span className={`inline-flex items-center justify-center w-7 h-7 rounded-full text-[10px] font-semibold ${toneFor(a.name)}`}>{initials(a.name)}</span>
              <span className="min-w-0 flex-1">
                <span className="block text-sm text-slate-800 truncate">{a.name}</span>
                <span className="block text-[11px] text-slate-400 truncate">{a.email || a.phone || `NPN ${a.id}`}</span>
              </span>
              <span className={`shrink-0 w-5 h-5 rounded-full border flex items-center justify-center ${on ? 'bg-sky-600 border-sky-600' : 'border-slate-300'}`}>
                {on && <Check size={12} className="text-white" />}
              </span>
            </button>
          );
        })}
      </div>
    </div>
  );
}

/* ─────────────────────── Agency downline tree ─────────────────────── */
function DownlinePicker({ selected, onChange }) {
  const [tree, setTree] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expanded, setExpanded] = useState({});

  useEffect(() => {
    (async () => {
      try { const r = await broadcastAPI.agenciesTree(); setTree(r.data.items || []); }
      catch { setTree([]); }
      finally { setLoading(false); }
    })();
  }, []);

  // Build children map + roots
  const { childrenOf, roots, byId } = useMemo(() => {
    const kids = {}; const ids = {}; const rts = [];
    tree.forEach((a) => { ids[a.agency_id] = a; (kids[a.parent_agency_id || '__root'] ||= []).push(a); });
    (kids['__root'] || []).forEach((a) => rts.push(a));
    return { childrenOf: kids, roots: rts, byId: ids };
  }, [tree]);

  const descendantCount = useCallback((id) => {
    let total = byId[id]?.direct_agents || 0;
    (childrenOf[id] || []).forEach((c) => { total += descendantCount(c.agency_id); });
    return total;
  }, [childrenOf, byId]);

  const sel = useMemo(() => new Set(selected), [selected]);
  const toggle = (id) => {
    const next = new Set(sel);
    if (next.has(id)) next.delete(id); else next.add(id);
    onChange(Array.from(next));
  };

  const renderNode = (a, depth = 0) => {
    const kids = childrenOf[a.agency_id] || [];
    const isOpen = expanded[a.agency_id] ?? depth < 1;
    const on = sel.has(a.agency_id);
    return (
      <div key={a.agency_id}>
        <div className={`flex items-center gap-1.5 rounded-lg pr-2 py-1.5 transition-colors ${on ? 'bg-emerald-50' : 'hover:bg-slate-50'}`}
             style={{ paddingLeft: `${8 + depth * 18}px` }} data-testid={`downline-node-${a.agency_id}`}>
          {kids.length > 0 ? (
            <button type="button" onClick={() => setExpanded((p) => ({ ...p, [a.agency_id]: !isOpen }))}
                    className="text-slate-400 hover:text-slate-700">
              <ChevronRight size={14} className={`transition-transform ${isOpen ? 'rotate-90' : ''}`} />
            </button>
          ) : <span className="w-[14px]" />}
          <button type="button" onClick={() => toggle(a.agency_id)} data-testid={`downline-toggle-${a.agency_id}`}
                  className="flex-1 min-w-0 flex items-center gap-2 text-left">
            <span className={`shrink-0 w-4 h-4 rounded border flex items-center justify-center ${on ? 'bg-emerald-600 border-emerald-600' : 'border-slate-300'}`}>
              {on && <Check size={11} className="text-white" />}
            </span>
            <span className="truncate text-sm text-slate-800">{a.agency_name}</span>
            <span className="shrink-0 inline-flex items-center gap-1 text-[10px] text-slate-400"><Users size={10} /> {descendantCount(a.agency_id)}</span>
            {on && <Badge className="bg-emerald-100 text-emerald-700 border-emerald-200 border text-[9px] font-normal">+ downline</Badge>}
          </button>
        </div>
        {isOpen && kids.map((c) => renderNode(c, depth + 1))}
      </div>
    );
  };

  if (loading) return <div className="p-4 text-center text-slate-400 text-xs"><Loader2 size={14} className="inline animate-spin mr-1" /> Loading agencies…</div>;
  if (tree.length === 0) return <div className="p-4 text-center text-slate-400 text-xs">No agencies found.</div>;

  return (
    <div className="space-y-2">
      <p className="text-[11px] text-slate-500 flex items-center gap-1">
        <Network size={12} /> Selecting an agency targets it <b>and its entire downline</b> — every sub-agency and their agents.
      </p>
      {selected.length > 0 && (
        <div className="flex items-center justify-between text-[11px]">
          <span className="text-emerald-700 font-medium">{selected.length} downline{selected.length === 1 ? '' : 's'} selected</span>
          <button type="button" onClick={() => onChange([])} className="text-slate-400 hover:text-rose-500 underline">clear</button>
        </div>
      )}
      <div className="max-h-64 overflow-y-auto rounded-lg border border-slate-100 py-1">
        {roots.map((r) => renderNode(r, 0))}
      </div>
    </div>
  );
}
